import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useT } from '@dia/i18n';
import { useAuth } from '../../auth';
import { HOSTS_WHATSAPP } from '../../env';
import { hostRowState, type HelpRowState } from '../logic/helpSheet';
import type { ComposerMode } from '../logic/helpThread';
import { lockReasonKey } from '../logic/tileFaces';
import { isPlaceholderNumber, normalizeWhatsAppNumber } from '../logic/whatsapp';
import {
  AidIcon,
  AlertIcon,
  BellIcon,
  ChevronIcon,
  CloseIcon,
  FlagIcon,
  LockIcon,
  MessageIcon,
  PhoneIcon,
} from './shellIcons';
import { HelpThread } from './HelpThread';
import { useAccess } from './useAccess';
import { useUnreadReplies } from './useUnreadReplies';

/**
 * The round exclamation-mark button, bottom right on every guest screen, and
 * the help sheet it opens (docs/GUEST-REDESIGN.md, section 2).
 *
 * It lives in the shell, above the routes, so it is on every screen without
 * any screen knowing about it, and the sheet slides up over whatever the guest
 * was doing so they never lose their place.
 *
 * What never depends on the network or a login: the crew emergency row (once
 * a real number is set) and Safety (a public page in the bundle). There is no
 * 911 fallback here: Panama's emergency line does not reach this island, so
 * the crew are the whole system. The rows that reach the hosts say "sign in to
 * send" with no session, and give the closed-window reason outside the window.
 *
 * Phase 3: "Ask for something", "Report a problem" and "Messages" no longer
 * leave the sheet. Each opens the one help thread right here, with its composer
 * set to that way in, and a back button returns to the list of rows.
 *
 * Spacing: the button must never sit on top of words, at rest or mid-scroll,
 * and never on the iPhone home bar (audit 2026-09-26, F3). Padding the foot of
 * the page alone only cleared the last item once the guest had scrolled all
 * the way down; everything that passed under the button on the way there was
 * covered. So the button stands on its own strip: a band across the bottom of
 * the screen in the page's ground color, the same idea as the shell's sticky
 * footer, that the page scrolls up out of like it would out of a tab bar. Its
 * top edge fades rather than cutting a line in half, it reaches past the home
 * bar, and it takes taps so nothing half hidden under it can be hit by
 * accident. Every page's main column still gets empty space at its foot, a
 * little taller than the band, so the last thing on it scrolls clear.
 * Guarded by e2e/help-button-overlap.mjs.
 */

const INTRODUCED = 'dia.guest.help.introduced';
/** How long the word "Help" rides beside the button on the very first open. */
const WORD_MS = 4000;

function alreadyIntroduced(): boolean {
  try {
    return globalThis.localStorage?.getItem(INTRODUCED) === '1';
  } catch {
    return true;
  }
}

/*
 * The band: solid from the screen's bottom edge to half a rem above the
 * button's top (1rem lift + 3.5rem button + 0.5rem), then a 1rem fade above
 * that. The fade is drawn by ::before so the band's own box, the part that can
 * sit behind the button, is fully opaque. #main's foot padding stays taller
 * than band plus fade.
 */
const CSS = `
#main { padding-bottom: calc(7rem + env(safe-area-inset-bottom)); }
.dia-help-band {
  position: fixed; left: 0; right: 0; bottom: 0; z-index: 30;
  height: calc(5rem + env(safe-area-inset-bottom));
  background: rgb(var(--c-ground));
}
.dia-help-band::before {
  content: ''; position: absolute; left: 0; right: 0; bottom: 100%; height: 1rem;
  background: linear-gradient(to bottom, rgb(var(--c-ground) / 0), rgb(var(--c-ground)));
  pointer-events: none;
}
@keyframes dia-sheet-up { from { transform: translateY(100%); } to { transform: none; } }
@keyframes dia-scrim-in { from { opacity: 0; } to { opacity: 1; } }
.dia-sheet { animation: dia-sheet-up 250ms cubic-bezier(.2,.7,.2,1) both; }
.dia-scrim { animation: dia-scrim-in 200ms ease-out both; }
@media (prefers-reduced-motion: reduce) {
  .dia-sheet, .dia-scrim { animation: none !important; }
}
`;

export function HelpButton() {
  const { t } = useT();
  const { pathname, search } = useLocation();
  const unread = useUnreadReplies();
  const [open, setOpen] = useState(false);
  const [wordShown, setWordShown] = useState(() => !alreadyIntroduced());
  const buttonRef = useRef<HTMLButtonElement>(null);

  // First open ever: "Help" beside the circle for a few seconds, then just the circle.
  useEffect(() => {
    if (!wordShown) return;
    const id = setTimeout(() => {
      setWordShown(false);
      try {
        globalThis.localStorage?.setItem(INTRODUCED, '1');
      } catch {
        // Private mode. The word shows again next visit, which is harmless.
      }
    }, WORD_MS);
    return () => clearTimeout(id);
  }, [wordShown]);

  // Going somewhere from the sheet closes it.
  useEffect(() => {
    setOpen(false);
  }, [pathname, search]);

  const close = useCallback(() => {
    setOpen(false);
    // Give focus back to the button that opened it.
    requestAnimationFrame(() => buttonRef.current?.focus());
  }, []);

  const label = unread > 0 ? t('guest.help.button_unread') : t('guest.help.button');

  return (
    <>
      <style>{CSS}</style>
      <div className="dia-help-band" aria-hidden="true" />
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className={`fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-[max(1rem,env(safe-area-inset-right))] z-40 flex h-14 min-w-[3.5rem] items-center justify-center rounded-full border-[3px] border-ground bg-accent text-onAccent ${
          wordShown ? 'pl-3 pr-5' : 'px-0'
        }`}
      >
        <AlertIcon className="h-7 w-7" />
        <span
          aria-hidden="true"
          className={`overflow-hidden whitespace-nowrap font-semibold transition-[max-width,opacity] duration-300 motion-reduce:transition-none ${
            wordShown ? 'max-w-[8rem] opacity-100' : 'max-w-0 opacity-0'
          }`}
        >
          {t('guest.help.word')}
        </span>
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-ground bg-mark"
          />
        )}
      </button>

      {open && <HelpSheet unread={unread} onClose={close} onNavigate={() => setOpen(false)} />}
    </>
  );
}

function HelpSheet({
  unread,
  onClose,
  onNavigate,
}: {
  unread: number;
  onClose: () => void;
  /** A row was followed. Close without pulling focus back, the new page takes it. */
  onNavigate: () => void;
}) {
  const { t } = useT();
  const { session } = useAuth();
  const access = useAccess();
  const titleId = useId();
  const sheetRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  // The list of rows, or the thread opened on one way in.
  const [thread, setThread] = useState<ComposerMode | null>(null);

  const state: HelpRowState = hostRowState(Boolean(session), access?.personalOpen ?? false);
  const reason =
    session && access ? t(lockReasonKey(access.phase, session.stay.status === 'cancelled')) : '';
  const crewDigits = normalizeWhatsAppNumber(HOSTS_WHATSAPP);
  const crewReady = Boolean(crewDigits && !isPlaceholderNumber(crewDigits));

  // Focus goes into the sheet, and the page behind it stops scrolling.
  useEffect(() => {
    closeRef.current?.focus();
    const body = document.body;
    const before = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = before;
    };
  }, []);

  // Opening the thread moves focus to its back button; going back returns it to the top.
  useEffect(() => {
    if (thread) backRef.current?.focus();
    else closeRef.current?.focus();
    sheetRef.current?.scrollTo?.({ top: 0 });
  }, [thread]);

  // Escape closes; Tab stays inside the sheet while it is open.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== 'Tab' || !sheetRef.current) return;
    const focusable = sheetRef.current.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), summary',
    );
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const hostHint = (openHint: string) =>
    state === 'sign_in' ? t('guest.help.sign_in') : state === 'closed' ? reason : openHint;

  return (
    <div className="fixed inset-0 z-50" onKeyDown={onKeyDown}>
      <div className="dia-scrim absolute inset-0 bg-surfaceDeep/70" aria-hidden="true" onClick={onClose} />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="dia-sheet absolute inset-x-0 bottom-0 mx-auto max-h-[88dvh] w-full max-w-[32rem] overflow-y-auto rounded-t-[1.75rem] border-t border-line bg-surface px-[1.125rem] pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-2.5"
      >
        <div aria-hidden="true" className="mx-auto mb-3.5 h-1 w-[2.625rem] rounded-full bg-ink/30" />

        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {thread && (
              <button
                ref={backRef}
                type="button"
                onClick={() => setThread(null)}
                className="-ml-1 mb-1 inline-flex min-h-tap items-center gap-1 px-1 text-accent underline underline-offset-4"
              >
                {'‹'} {t('guest.thread.back')}
              </button>
            )}
            <h2 id={titleId} className="font-display text-[1.6em] leading-tight text-ink">
              {thread ? t('guest.thread.title') : t('guest.help.title')}
            </h2>
            <p className="mt-0.5 text-caption text-inkFaint">
              {thread ? t('guest.thread.lede') : t('guest.help.sub')}
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t('action.close')}
            className="-mr-2 -mt-1 grid min-h-tap min-w-tap place-items-center text-inkMuted hover:text-ink"
          >
            <CloseIcon className="h-6 w-6" />
          </button>
        </div>

        {thread && (
          <div className="mt-4">
            <HelpThread initialMode={thread} />
          </div>
        )}

        {!thread && (
          <>
            {/* The crew are the whole emergency system here: no 911 fallback,
                because it does not reach this island. Never depends on the
                network or a login, same as Safety below it. */}
            {crewReady ? (
              <a
                href={`tel:+${crewDigits}`}
                className="mt-4 flex min-h-[4.5rem] items-center gap-3.5 rounded-[1.125rem] border-[1.5px] border-accent bg-accent/10 px-4 py-3.5"
              >
                <span className="grid h-[2.875rem] w-[2.875rem] shrink-0 place-items-center rounded-[0.95rem] bg-accent/15 text-accent">
                  <PhoneIcon className="h-6 w-6" />
                </span>
                <span className="flex min-w-0 flex-col">
                  <strong className="font-display text-[1.25em] font-medium leading-tight text-ink">
                    {t('guest.help.crew_call')}
                  </strong>
                  <span className="text-caption text-inkMuted">{t('guest.help.crew_call_hint')}</span>
                </span>
              </a>
            ) : (
              <p className="mt-4 rounded-[1.125rem] border border-line px-4 py-3.5 text-caption text-inkMuted">
                {t('guest.help.crew_pending')}
              </p>
            )}

            <ul className="mt-2.5 list-none p-0">
              <HelpRow
                to="/seguridad"
                icon={<AidIcon className="h-[1.35rem] w-[1.35rem]" />}
                title={t('guest.help.safety')}
                hint={t('guest.help.safety_hint')}
                state="open"
                onNavigate={onNavigate}
              />
              <HelpRow
                to="/entrar"
                onOpen={() => setThread('ask')}
                icon={<BellIcon className="h-[1.35rem] w-[1.35rem]" />}
                title={t('guest.help.ask')}
                hint={hostHint(t('guest.help.ask_hint'))}
                state={state}
                onNavigate={onNavigate}
              />
              <HelpRow
                to="/entrar"
                onOpen={() => setThread('problem')}
                icon={<FlagIcon className="h-[1.35rem] w-[1.35rem]" />}
                title={t('guest.help.problem')}
                hint={hostHint(t('guest.help.problem_hint'))}
                state={state}
                onNavigate={onNavigate}
              />
              <HelpRow
                to="/entrar"
                onOpen={() => setThread('message')}
                icon={<MessageIcon className="h-[1.35rem] w-[1.35rem]" />}
                title={t('guest.help.messages')}
                hint={hostHint(t('guest.help.messages_hint'))}
                state={state}
                onNavigate={onNavigate}
                badge={
                  unread > 0 ? (
                    <span className="grid h-[1.4rem] min-w-[1.4rem] place-items-center rounded-full bg-mark px-1.5 text-caption font-bold leading-none text-onAccent">
                      <span aria-hidden="true">{unread}</span>
                      <span className="sr-only">{t('guest.help.unread', { count: unread })}</span>
                    </span>
                  ) : null
                }
              />
            </ul>

            <p className="mt-3 border-t border-line pt-3 text-caption leading-relaxed text-inkFaint">
              {t('guest.help.fineprint')}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * One row of the sheet. A row with `onOpen` opens the help thread inside the
 * sheet when it is open; with no session it links to `to` (sign in) instead.
 * A row with only `to` is a plain link (Safety). Outside the window it is not
 * a link at all: a lock and the reason, never a dead end that looks alive.
 */
function HelpRow({
  to,
  onOpen,
  icon,
  title,
  hint,
  state,
  badge,
  onNavigate,
}: {
  to: string;
  onOpen?: () => void;
  icon: ReactNode;
  title: string;
  hint: string;
  state: HelpRowState;
  badge?: ReactNode;
  onNavigate: () => void;
}) {
  const closed = state === 'closed';
  const body = (
    <>
      <span
        className={`grid h-[2.625rem] w-[2.625rem] shrink-0 place-items-center rounded-[0.875rem] ${
          closed ? 'bg-ink/5 text-inkFaint' : 'bg-accent/10 text-accent'
        }`}
      >
        {closed ? <LockIcon className="h-[1.2rem] w-[1.2rem]" /> : icon}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className={`font-semibold leading-tight ${closed ? 'text-inkMuted' : 'text-ink'}`}>{title}</span>
        <span className="mt-0.5 text-caption leading-snug text-inkFaint">{hint}</span>
      </span>
      {badge}
      {!closed && <ChevronIcon className="h-5 w-5 shrink-0 text-inkFaint" />}
    </>
  );

  const row = 'flex min-h-[4.25rem] items-center gap-3.5 px-0.5 py-2.5';

  return (
    <li className="border-t border-line first:border-t-0">
      {closed ? (
        <div aria-disabled="true" className={row}>
          {body}
        </div>
      ) : onOpen && state === 'open' ? (
        <button type="button" onClick={onOpen} className={`${row} w-full text-left hover:text-accent`}>
          {body}
        </button>
      ) : (
        <Link to={to} onClick={onNavigate} className={`${row} hover:text-accent`}>
          {body}
        </Link>
      )}
    </li>
  );
}
